import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCartStore, type CartLineModifier } from '../stores/cart.store.js'
import { useSessionStore } from '../stores/session.store.js'
import { formatMoney } from '../lib/money.js'
import { fetchActiveMenu, fetchProductModifiers, type MenuItemDto, type ModifierGroupDto } from '../lib/api.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'

interface LocationState { item?: MenuItemDto }

/** Una opción seleccionada, indexada por su grupo para validar min/max. */
interface PickedOption extends CartLineModifier { groupId: string }

/** Etiqueta de regla de un grupo — "Requerido · Elige 1", "Hasta 3", etc.
 *  Mismo criterio que el POS (AddItemDialog). */
function groupRuleLabel(g: ModifierGroupDto, count: number): string {
  const required = g.isRequired ? 'Requerido · ' : ''
  const choose =
    g.maxSelect === 1
      ? 'Elige 1'
      : g.minSelect === g.maxSelect
        ? `Elige ${g.maxSelect}`
        : `Hasta ${g.maxSelect}`
  return `${required}${choose}${count > 0 ? ` · ${count} sel.` : ''}`
}

export function ItemDetail() {
  const { slug, productId } = useParams<{ slug: string; productId: string }>()
  const navigate = useNavigate()
  const loc = useLocation()
  const stateItem = (loc.state as LocationState | null)?.item
  const session = useSessionStore((s) => s.session)
  const add = useCartStore((s) => s.add)
  const [qty, setQty] = useState(1)
  const [notes, setNotes] = useState('')
  const [selected, setSelected] = useState<Map<string, PickedOption>>(() => new Map())
  const [error, setError] = useState<string | null>(null)

  const tenantId = session?.tenantId

  // Deep-link / refresh: si NO llegamos desde el menú (sin loc.state.item, p.ej.
  // un enlace compartido o un F5 en /item/:productId), resolvemos el producto del
  // menú público por id en vez de mostrar "Producto no encontrado".
  const itemQuery = useQuery({
    enabled: !stateItem && !!tenantId && !!productId,
    queryKey: ['menu-item', tenantId, productId],
    queryFn: async () => {
      const r = await fetchActiveMenu(tenantId!, session?.token ?? '')
      if (!r.ok) throw new Error(`${r.error.code}: ${r.error.message}`)
      const all = r.data.menu.sections.flatMap((s) => s.items)
      return all.find((i) => i.id === productId) ?? null
    },
    staleTime: 60_000,
  })
  const item: MenuItemDto | undefined = stateItem ?? itemQuery.data ?? undefined

  // Grupos de modificadores del producto — mismo endpoint que consume el POS.
  const modifiersQuery = useQuery({
    enabled: !!item && !!tenantId,
    queryKey: ['modifiers', tenantId, item?.id],
    queryFn: async () => {
      const r = await fetchProductModifiers(tenantId!, item!.id, session?.token ?? '')
      if (!r.ok) throw new Error(`${r.error.code}: ${r.error.message}`)
      return r.data.items
        .filter((g) => g.active && !g.deletedAt)
        .map((g) => ({
          ...g,
          options: g.options
            .filter((o) => o.active)
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder),
        }))
    },
    staleTime: 60_000,
  })
  const groups: ModifierGroupDto[] = modifiersQuery.data ?? []

  useEffect(() => {
    setQty(1)
    setNotes('')
    setSelected(new Map())
    setError(null)
  }, [item?.id])

  // TODOS los hooks van ANTES del early return: `item` ahora transiciona
  // undefined→definido (deep-link), y un hook después del return rompía el orden
  // de hooks (React #310). modDelta solo depende de `selected`.
  const modDelta = useMemo(
    () => [...selected.values()].reduce((s, o) => s + o.priceDeltaCents, 0),
    [selected],
  )

  if (!item) {
    // itemQuery sólo corre con tenantId (sesión persistida). Sin sesión → no
    // está cargando → cae a "no encontrado" en vez de spinner infinito.
    const resolving = itemQuery.isLoading
    return (
      <div className="app">
        <NintaiGOHeader slug={slug} />
        <div className="container">
          <div className="card" style={{ textAlign: 'center' }}>
            {resolving ? (
              <>
                <span className="spinner" />
                <div className="muted" style={{ marginTop: 8 }}>Cargando producto…</div>
              </>
            ) : (
              <>
                <p>Producto no encontrado.</p>
                <button className="btn btn-primary" onClick={() => navigate(`/r/${slug}/menu`)}>
                  Volver al menú
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  function toggleOption(group: ModifierGroupDto, opt: ModifierGroupDto['options'][number]): void {
    setError(null)
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(opt.id)) {
        next.delete(opt.id)
        return next
      }
      const inGroup = [...next.values()].filter((o) => o.groupId === group.id)
      if (group.maxSelect === 1) {
        // single-select: la nueva opción reemplaza a la anterior
        for (const e of inGroup) next.delete(e.modifierId)
      } else if (inGroup.length >= group.maxSelect) {
        return prev // tope alcanzado — ignora
      }
      next.set(opt.id, {
        modifierId: opt.id,
        name: opt.label,
        priceDeltaCents: opt.priceDeltaCents,
        groupId: group.id,
      })
      return next
    })
  }

  /** Valida required + min/max de cada grupo. Devuelve mensaje o null. */
  function validate(): string | null {
    for (const g of groups) {
      const count = [...selected.values()].filter((o) => o.groupId === g.id).length
      const min = g.isRequired ? Math.max(1, g.minSelect) : g.minSelect
      if (count < min) return `Selecciona al menos ${min} en "${g.name}"`
      if (count > g.maxSelect) return `Máximo ${g.maxSelect} en "${g.name}"`
    }
    return null
  }

  const unitWithMods = item.priceCents + modDelta
  const lineTotal = unitWithMods * qty

  function onAdd(): void {
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    add({
      productId: item!.id,
      productName: item!.name,
      unitPriceCents: item!.priceCents, // base — el carrito/orden suman los deltas
      quantity: qty,
      modifiers: [...selected.values()].map(({ modifierId, name, priceDeltaCents }) => ({
        modifierId,
        name,
        priceDeltaCents,
      })),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    })
    navigate(`/r/${slug}/menu`)
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} />
      <div className="container">
        <button
          className="btn btn-ghost"
          style={{ width: 'auto', marginBottom: 12 }}
          onClick={() => navigate(-1)}
        >
          ← Volver al menú
        </button>
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt={item.name}
            style={{
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: 'var(--radius)',
              marginBottom: 16,
              border: '1px solid var(--line)',
            }}
          />
        )}
        <div className="card">
          <h2 style={{ marginTop: 0, marginBottom: 6 }}>{item.name}</h2>
          <div style={{ color: 'var(--accent-2)', fontWeight: 600, marginBottom: 14 }}>
            {formatMoney(item.priceCents, item.currency)}
          </div>
          {item.description && (
            <p className="muted" style={{ marginBottom: 0 }}>{item.description}</p>
          )}
        </div>

        {/* ── Modificadores ─────────────────────────────────────────────── */}
        {modifiersQuery.isLoading && (
          <div className="card">
            <div className="muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="spinner" /> Cargando opciones…
            </div>
          </div>
        )}
        {modifiersQuery.isError && (
          <div className="card">
            <div className="muted">No se pudieron cargar las opciones. Puedes agregar el producto sin personalizar.</div>
          </div>
        )}
        {groups.map((g) => {
          const count = [...selected.values()].filter((o) => o.groupId === g.id).length
          return (
            <div className="card mod-group" key={g.id}>
              <div className="mod-group__head">
                <span className="mod-group__name">{g.name}</span>
                <span
                  className={`mod-group__rule${g.isRequired ? ' mod-group__rule--req' : ''}`}
                >
                  {groupRuleLabel(g, count)}
                </span>
              </div>
              <div className="mod-opts">
                {g.options.map((o) => {
                  const on = selected.has(o.id)
                  const single = g.maxSelect === 1
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`mod-opt${on ? ' mod-opt--on' : ''}`}
                      onClick={() => toggleOption(g, o)}
                    >
                      <span className={`mod-opt__tick mod-opt__tick--${single ? 'radio' : 'check'}`}>
                        {on ? (single ? '●' : '✓') : ''}
                      </span>
                      <span className="mod-opt__name">{o.label}</span>
                      {o.priceDeltaCents !== 0 && (
                        <span className="mod-opt__delta">
                          {o.priceDeltaCents > 0 ? '+' : '−'}
                          {formatMoney(Math.abs(o.priceDeltaCents), item.currency)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}

        <div className="card">
          <label className="label" style={{ marginTop: 0 }}>
            Notas (sin cebolla, término, etc.)
          </label>
          <input
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={200}
            placeholder="Opcional"
          />
        </div>

        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <span className="danger" style={{ fontSize: 13, fontWeight: 600 }}>{error}</span>
          </div>
        )}

        <div className="card row">
          <div className="qty">
            <button onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
            <span>{qty}</span>
            <button onClick={() => setQty(qty + 1)}>+</button>
          </div>
          <button className="btn btn-primary" style={{ width: 'auto' }} onClick={onAdd}>
            Agregar · {formatMoney(lineTotal, item.currency)}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .mod-group__head { display:flex; align-items:baseline; justify-content:space-between; gap:10px; margin-bottom:12px; }
  .mod-group__name { font-weight:600; font-size:15px; color:var(--fg); }
  .mod-group__rule { font-size:11px; color:var(--fg-muted); white-space:nowrap; text-transform:uppercase; letter-spacing:0.4px; font-weight:600; }
  .mod-group__rule--req { color:var(--accent-2); }
  .mod-opts { display:flex; flex-direction:column; gap:8px; }
  .mod-opt {
    display:flex; align-items:center; gap:10px;
    width:100%; min-height:48px; padding:10px 14px;
    border:1px solid var(--line-strong); border-radius:var(--radius-input);
    background:var(--bg-input); color:var(--fg);
    text-align:left; cursor:pointer;
    transition:background 120ms ease, border-color 120ms ease;
  }
  .mod-opt:active { transform:scale(0.99); }
  .mod-opt--on { background:var(--accent-soft); border-color:var(--accent); }
  .mod-opt__tick {
    flex:0 0 auto; width:22px; height:22px;
    display:flex; align-items:center; justify-content:center;
    border:1.5px solid var(--line-strong); color:var(--fg);
    font-size:12px; line-height:1;
  }
  .mod-opt__tick--radio { border-radius:50%; }
  .mod-opt__tick--check { border-radius:7px; }
  .mod-opt--on .mod-opt__tick { background:var(--accent); border-color:var(--accent); color:var(--accent-ink); }
  .mod-opt__name { flex:1; font-size:14.5px; line-height:1.25; }
  .mod-opt__delta { flex:0 0 auto; font-size:13px; font-weight:600; color:var(--accent-2); font-variant-numeric:tabular-nums; }
`
